import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { track } from "@/lib/analytics";

const CITIES = ["Johannesburg", "Cape Town", "Durban", "Pretoria", "Port Elizabeth", "Bloemfontein", "East London", "Polokwane", "Nelspruit", "Kimberley"];
const TYPES = [
  { v: "scrapyard", label: "Scrapyard" },
  { v: "dealership", label: "Dealership" },
  { v: "aftermarket", label: "Aftermarket Supplier" },
  { v: "workshop", label: "Workshop" },
  { v: "accessories", label: "Vehicle Accessories" },
  { v: "interior", label: "Interior" },
  { v: "wheels_tyres", label: "Wheels & Tyres" },
  { v: "offroad", label: "4x4 & Off-Road" },
  { v: "glass", label: "Glass & Visibility" },
];

export default function SupplierRegisterPage() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    business_name: "",
    city: "Johannesburg",
    type: "aftermarket",
    phone: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.business_name.trim() || !form.email.trim() || !form.password) {
      toast.error("Business name, email, and password are required");
      return;
    }
    if (form.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setBusy(true);
    try {
      const data = await register({ ...form, email: form.email.trim().toLowerCase() });
      track("sign_up", { method: "email", role: "supplier", supplier_type: form.type, city: form.city });
      toast.success("Welcome to Spannr!");
      nav(`/suppliers/dashboard/${data.supplier.id}`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Registration failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
      <div className="text-xs font-mono-tech tracking-[0.25em] text-[#FF5100] mb-2">
        SUPPLIERS · ONBOARD
      </div>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold leading-none">
        List your inventory on Spannr.
      </h1>
      <p className="mt-3 text-neutral-500 max-w-xl">
        Mechanics across South Africa search this network in real time. Get matched
        to live demand for your stock.
      </p>

      <form
        onSubmit={submit}
        data-testid="supplier-register-form"
        className="mt-8 space-y-5 border border-neutral-200 bg-neutral-50 p-6"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Your name *">
            <input
              data-testid="register-name-input"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g. Joe Mokoena"
              className="w-full bg-white border border-neutral-200 focus:border-[#FF5100] outline-none px-3 h-11 text-sm"
            />
          </Field>
          <Field label="Business Name *">
            <input
              data-testid="supplier-name-input"
              value={form.business_name}
              onChange={(e) => setForm((f) => ({ ...f, business_name: e.target.value }))}
              placeholder="e.g. Joe's Spares"
              className="w-full bg-white border border-neutral-200 focus:border-[#FF5100] outline-none px-3 h-11 text-sm"
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="Email *">
            <input
              type="email"
              data-testid="register-email-input"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              placeholder="you@yourcompany.co.za"
              className="w-full bg-white border border-neutral-200 focus:border-[#FF5100] outline-none px-3 h-11 text-sm"
            />
          </Field>
          <Field label="Password *">
            <input
              type="password"
              data-testid="register-password-input"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              placeholder="At least 6 characters"
              className="w-full bg-white border border-neutral-200 focus:border-[#FF5100] outline-none px-3 h-11 text-sm"
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field label="City">
            <select
              data-testid="supplier-city-select"
              value={form.city}
              onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))}
              className="w-full bg-white border border-neutral-200 focus:border-[#FF5100] outline-none px-3 h-11 text-sm"
            >
              {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Type">
            <select
              data-testid="supplier-type-select"
              value={form.type}
              onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
              className="w-full bg-white border border-neutral-200 focus:border-[#FF5100] outline-none px-3 h-11 text-sm"
            >
              {TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
            </select>
          </Field>
        </div>

        <Field label="Phone (used for WhatsApp click-to-chat)">
          <input
            data-testid="supplier-phone-input"
            value={form.phone}
            onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
            placeholder="+27 ..."
            className="w-full bg-white border border-neutral-200 focus:border-[#FF5100] outline-none px-3 h-11 text-sm"
          />
        </Field>

        <button
          type="submit"
          data-testid="supplier-submit-button"
          disabled={busy}
          className="w-full sm:w-auto px-6 h-11 bg-[#FF5100] hover:bg-[#E64A00] disabled:opacity-50 text-white font-bold uppercase tracking-wider text-xs transition-colors"
        >
          {busy ? "Submitting..." : "Register & Continue"}
        </button>

        <div className="text-xs text-neutral-500 pt-3 border-t border-neutral-200">
          Already have an account?{" "}
          <Link to="/login" className="text-[#FF5100] hover:underline" data-testid="register-login-link">
            Sign in
          </Link>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.2em] font-mono-tech text-neutral-500 mb-1.5">
        {label}
      </div>
      {children}
    </label>
  );
}
